import React, { useEffect, useState } from "react";
import { Mail, CheckCircle2, Clock, Paperclip, Search, Trash2, Loader2, X, FileText, Eye, CheckCheck } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_LABELS = {
  quittance: "Quittance",
  rappel_loyer: "Rappel loyer",
  rappel_assurance: "Rappel assurance",
  invitation_assurance: "Invitation assurance",
  rappel_signature: "Rappel signature",
  email: "Email",
};
const KIND_STYLE = {
  quittance: "bg-brand-lime/30 text-brand-greenDeep",
  rappel_loyer: "bg-amber-100 text-amber-700",
  rappel_assurance: "bg-blue-100 text-blue-700",
  invitation_assurance: "bg-purple-100 text-purple-700",
};

function DeliveryBadge({ log }) {
  if (log.opened) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600" data-testid={`delivery-opened-${log.id}`}><Eye className="w-3 h-3" /> Ouvert</span>;
  }
  if (log.delivered) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-green/70" data-testid={`delivery-received-${log.id}`}><CheckCheck className="w-3 h-3" /> Reçu</span>;
  }
  if (log.delivery_error) {
    return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600" data-testid={`delivery-error-${log.id}`}>Échec ({log.delivery_error})</span>;
  }
  return null;
}

export default function JournalEnvois() {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("Tous");
  const [selected, setSelected] = useState([]);
  const [confirmDel, setConfirmDel] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attachOnly, setAttachOnly] = useState(false);

  const load = () => api.get("/email-logs").then((r) => setLogs(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const openDetail = async (l) => {
    setDetailLoading(true);
    setDetail(l); // affiche immédiatement les infos de base
    try {
      const res = await api.get(`/email-logs/${l.id}`);
      setDetail(res.data);
    } catch {
      toast.error("Impossible de charger le détail de l'envoi");
    } finally { setDetailLoading(false); }
  };

  const kinds = ["Tous", ...Array.from(new Set(logs.map((l) => l.kind).filter(Boolean)))];
  const filtered = logs.filter((l) =>
    (filter === "Tous" || l.kind === filter) &&
    (!attachOnly || l.attachment || l.has_attachment) &&
    (!q || (l.to || "").toLowerCase().includes(q.toLowerCase()) || (l.subject || "").toLowerCase().includes(q.toLowerCase()))
  );

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = filtered.length > 0 && filtered.every((l) => selected.includes(l.id));
  const toggleSelAll = () => setSelected(allSelected ? [] : filtered.map((l) => l.id));

  const bulkDelete = async () => {
    if (!selected.length) return;
    setDelBusy(true);
    try {
      const res = await api.post("/email-logs/bulk-delete", { ids: selected });
      const ids = new Set(selected);
      setLogs((p) => p.filter((l) => !ids.has(l.id)));
      toast.success(`${res.data.deleted} envoi(s) supprimé(s)`);
      setSelected([]);
      setConfirmDel(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally { setDelBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <p className="text-brand-green/60">{filtered.length} envoi{filtered.length > 1 ? "s" : ""}{selected.length > 0 ? ` · ${selected.length} sélectionné(s)` : ""}</p>
        <div className="flex gap-2 flex-wrap">
          {selected.length > 0 && (
            <button onClick={() => setConfirmDel(true)} data-testid="journal-bulk-delete-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-sm transition-colors">
              <Trash2 className="w-4 h-4" /> Supprimer ({selected.length})
            </button>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-green/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..." className="pl-9 pr-4 py-2 rounded-full border border-brand-soft outline-none text-sm focus:border-brand-limeDark" data-testid="journal-search" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-2 rounded-full border border-brand-soft outline-none text-sm bg-white focus:border-brand-limeDark" data-testid="journal-filter">
            {kinds.map((k) => <option key={k} value={k}>{k === "Tous" ? "Tous les types" : (KIND_LABELS[k] || k)}</option>)}
          </select>
          <button type="button" onClick={() => setAttachOnly((v) => !v)} data-testid="journal-filter-attachment" aria-pressed={attachOnly} title="N'afficher que les envois avec pièce jointe" className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${attachOnly ? "bg-brand-lime border-brand-limeDark text-brand-greenDeep" : "bg-white border-brand-soft text-brand-green/70 hover:bg-brand-softer"}`}>
            <Paperclip className="w-4 h-4" /> Avec pièce jointe
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden">
        {filtered.length === 0 && <p className="text-center text-brand-green/50 py-16">Aucun envoi enregistré pour le moment.</p>}
        {filtered.length > 0 && (
          <div className="flex items-center gap-4 px-5 py-3 border-b border-brand-soft bg-brand-softer/40">
            <input type="checkbox" checked={allSelected} onChange={toggleSelAll} data-testid="journal-select-all" className="w-4 h-4 accent-red-500 cursor-pointer" title="Tout sélectionner" />
            <span className="text-xs font-medium text-brand-green/50">Tout sélectionner</span>
          </div>
        )}
        {filtered.map((l) => (
          <div key={l.id} className={`flex items-center gap-4 px-5 py-4 border-b border-brand-soft last:border-0 hover:bg-brand-softer/50 transition-colors ${selected.includes(l.id) ? "bg-red-50/40" : ""}`} data-testid={`journal-row-${l.id}`}>
            <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggleSel(l.id)} data-testid={`journal-select-${l.id}`} className="w-4 h-4 accent-red-500 cursor-pointer shrink-0" />
            <button type="button" onClick={() => openDetail(l)} data-testid={`journal-open-${l.id}`} title="Voir le détail de l'envoi" className="flex items-center gap-4 flex-1 min-w-0 text-left cursor-pointer">
              <span className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-brand-green" /></span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-brand-greenDeep truncate">{l.subject || "—"}</p>
                <p className="text-sm text-brand-green/50 truncate flex items-center gap-2">
                  {l.to}
                  {l.attachment && <span className="inline-flex items-center gap-1 text-xs text-brand-green/40"><Paperclip className="w-3 h-3" />{l.attachment}</span>}
                </p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold shrink-0 ${KIND_STYLE[l.kind] || "bg-brand-softer text-brand-green/70"}`}>{KIND_LABELS[l.kind] || l.kind || "Email"}</span>
              <div className="text-right w-32 shrink-0">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${l.sent ? "text-brand-greenDeep" : "text-amber-600"}`}>
                  {l.sent ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  {l.sent ? "Envoyé" : (l.simulated ? "Simulé" : "En attente")}
                </span>
                <div className="mt-0.5"><DeliveryBadge log={l} /></div>
                <p className="text-xs text-brand-green/40 mt-0.5">{l.created_at ? new Date(l.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : ""}</p>
              </div>
            </button>
          </div>
        ))}
      </div>
      {logs.some((l) => l.simulated) && (
        <p className="text-xs text-brand-green/50 text-center">« Simulé » : email journalisé sans envoi réel. Ajoutez une clé SendGrid pour activer l'envoi.</p>
      )}

      {detail && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="journal-detail-modal">
            <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-brand-soft sticky top-0 bg-white z-10">
              <div className="min-w-0">
                <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${KIND_STYLE[detail.kind] || "bg-brand-softer text-brand-green/70"}`}>{KIND_LABELS[detail.kind] || detail.kind || "Email"}</span>
                <h3 className="font-display text-xl font-semibold text-brand-greenDeep mt-2 break-words" data-testid="journal-detail-subject">{detail.subject || "—"}</h3>
              </div>
              <button onClick={() => setDetail(null)} className="text-brand-green/40 hover:text-brand-greenDeep shrink-0" data-testid="journal-detail-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 pt-5 space-y-5">
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><p className="text-xs text-brand-green/45 mb-0.5">Destinataire</p><p className="text-brand-greenDeep font-medium break-all" data-testid="journal-detail-to">{detail.to || "—"}</p></div>
                <div><p className="text-xs text-brand-green/45 mb-0.5">Date d'envoi</p><p className="text-brand-greenDeep font-medium">{detail.created_at ? new Date(detail.created_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }) : "—"}</p></div>
                <div><p className="text-xs text-brand-green/45 mb-0.5">Statut</p>
                  <span className={`inline-flex items-center gap-1 text-sm font-semibold ${detail.sent ? "text-brand-greenDeep" : "text-amber-600"}`}>
                    {detail.sent ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    {detail.sent ? "Envoyé" : (detail.simulated ? "Simulé (non envoyé)" : "En attente")}
                  </span>
                </div>
                <div data-testid="journal-detail-tracking"><p className="text-xs text-brand-green/45 mb-0.5">Suivi de délivrabilité</p>
                  {detail.opened ? (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600"><Eye className="w-4 h-4" /> Ouvert{detail.opened_at ? ` le ${new Date(detail.opened_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}</span>
                  ) : detail.delivered ? (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-green/70"><CheckCheck className="w-4 h-4" /> Reçu{detail.delivered_at ? ` le ${new Date(detail.delivered_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""}</span>
                  ) : detail.delivery_error ? (
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-red-600">Échec de remise ({detail.delivery_error})</span>
                  ) : (
                    <span className="text-sm text-brand-green/50">{detail.simulated ? "Non suivi (envoi simulé)" : "En attente de confirmation…"}</span>
                  )}
                </div>
                {(detail.attachment || detail.has_attachment) && (
                  <div><p className="text-xs text-brand-green/45 mb-0.5">Pièce jointe</p>
                    <a href={`${BACKEND_URL}/api/email-logs/${detail.id}/attachment`} target="_blank" rel="noreferrer" data-testid="journal-detail-attachment" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-xs font-semibold transition-colors">
                      <Paperclip className="w-3.5 h-3.5" /> {detail.attachment || "Ouvrir la pièce jointe"}
                    </a>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs text-brand-green/45 mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Contenu du message</p>
                {detailLoading ? (
                  <div className="flex items-center gap-2 text-sm text-brand-green/50 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>
                ) : detail.html ? (
                  <div className="rounded-2xl border border-brand-soft bg-brand-softer/30 p-3 overflow-x-auto" data-testid="journal-detail-body">
                    <div dangerouslySetInnerHTML={{ __html: detail.html }} />
                  </div>
                ) : (
                  <p className="text-sm text-brand-green/50 py-6 text-center rounded-2xl border border-dashed border-brand-soft" data-testid="journal-detail-nobody">Le contenu détaillé n'est pas disponible pour cet envoi (envoi antérieur à cette fonctionnalité).</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => !delBusy && setConfirmDel(false)}>
          <div className="bg-white rounded-3xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="journal-bulk-delete-modal">
            <div className="p-6 pb-4 border-b border-brand-soft flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center shrink-0"><Trash2 className="w-5 h-5 text-red-500" /></span>
              <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Supprimer {selected.length} envoi(s) ?</h3>
            </div>
            <div className="p-6 pt-5 space-y-4">
              <p className="text-sm text-brand-green/70">Cette action est définitive et retire les envois sélectionnés du journal. Les emails déjà envoyés ne sont pas rappelés.</p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDel(false)} disabled={delBusy} className="flex-1 py-3 rounded-full border border-brand-soft text-brand-greenDeep font-semibold hover:bg-brand-softer transition-colors disabled:opacity-60" data-testid="journal-bulk-delete-cancel">Annuler</button>
                <button onClick={bulkDelete} disabled={delBusy} className="flex-1 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60" data-testid="journal-bulk-delete-confirm">
                  {delBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
